/**
 * Templates the ShopService draws from to generate trade offers. Each template
 * declares the resource shape it produces; ShopService filters templates by
 * the player's current wallet (so we never offer skills they already own and
 * never offer to buy back skills they haven't earned).
 *
 * Why templates instead of pure RNG: raw RNG produces nonsense (e.g. "1 geo
 * for double-jump"). Templates keep cost/receive proportional and let us
 * balance per-archetype.
 */

import type { Resource, SkillId } from "../types";

import type { SeededRng } from "./seededRng";

export interface OfferTemplate {
  id: string;
  /** Selection weight; higher = more often picked. */
  weight: number;
  /** Player must own these skills for the template to be eligible. */
  requiresSkill?: SkillId[];
  /** Player must NOT own these skills (otherwise the offer is redundant). */
  forbidsSkill?: SkillId[];
  /** Player must currently hold at least this much inventory free space. */
  requiresFreeInventory?: number;
  build: (rng: SeededRng) => { cost: Resource[]; receive: Resource[] };
}

/**
 * Per-archetype baselines. Each numeric resource has a CENTRAL price; the
 * `band` helper draws within ±20 % of that center so the same template can
 * produce a range of offers around its target value (matches the spec's
 * "±20 % price band" tuning pass).
 */
const BAND = 0.2; // ±20 %

function band(rng: SeededRng, center: number): number {
  if (center <= 0) return 0;
  const lo = Math.max(1, Math.round(center * (1 - BAND)));
  const hi = Math.max(lo + 1, Math.round(center * (1 + BAND)));
  return rng.intRange(lo, hi + 1);
}

export const SHOP_OFFER_TEMPLATES: readonly OfferTemplate[] = [
  {
    id: "coin_for_heart",
    weight: 4,
    build: (rng) => ({
      cost: [{ kind: "geo", amount: band(rng, 45) }],
      receive: [{ kind: "hp", amount: 1 }],
    }),
  },
  {
    id: "coin_for_feather",
    weight: 3,
    requiresSkill: ["escapeFeather"],
    build: (rng) => ({
      cost: [{ kind: "geo", amount: band(rng, 35) }],
      receive: [{ kind: "feather", amount: 1 }],
    }),
  },
  {
    id: "feather_bundle",
    weight: 2,
    requiresSkill: ["escapeFeather"],
    build: (rng) => ({
      cost: [
        { kind: "geo", amount: band(rng, 70) },
        { kind: "inventoryItem", itemId: "scroll" },
      ],
      receive: [{ kind: "feather", amount: 2 }],
    }),
  },
  {
    id: "hp_for_coin",
    weight: 2,
    build: (rng) => ({
      cost: [{ kind: "hp", amount: 1 }],
      receive: [{ kind: "geo", amount: band(rng, 32) }],
    }),
  },
  {
    id: "scroll_dump",
    weight: 3,
    build: (rng) => ({
      cost: [{ kind: "inventoryItem", itemId: "scroll" }],
      receive: [{ kind: "geo", amount: band(rng, 13) }],
    }),
  },
  {
    id: "inventory_combo",
    weight: 1,
    build: (rng) => ({
      cost: [
        { kind: "inventoryItem", itemId: "scroll" },
        { kind: "inventoryItem", itemId: "scroll" },
      ],
      receive: [
        { kind: "hp", amount: 1 },
        // Feather count rounds 1-or-2 inside the ±20 % band around 1.5.
        { kind: "feather", amount: Math.max(1, Math.round(band(rng, 1.5))) },
      ],
    }),
  },
  {
    id: "doublejump_buyout",
    weight: 1,
    requiresSkill: ["doubleJump"],
    build: (rng) => ({
      cost: [{ kind: "skill", skillId: "doubleJump" }],
      receive: [
        { kind: "geo", amount: band(rng, 110) },
        { kind: "hp", amount: 1 },
      ],
    }),
  },
  {
    id: "doublejump_buy",
    weight: 1,
    forbidsSkill: ["doubleJump"],
    build: (rng) => ({
      cost: [
        { kind: "geo", amount: band(rng, 150) },
        { kind: "hp", amount: 1 },
      ],
      receive: [{ kind: "skill", skillId: "doubleJump" }],
    }),
  },
  {
    id: "feather_buyout",
    weight: 1,
    requiresSkill: ["escapeFeather"],
    build: (rng) => ({
      cost: [{ kind: "skill", skillId: "escapeFeather" }],
      receive: [{ kind: "geo", amount: band(rng, 90) }],
    }),
  },
  {
    id: "feather_buy",
    weight: 1,
    forbidsSkill: ["escapeFeather"],
    build: (rng) => ({
      cost: [{ kind: "geo", amount: band(rng, 110) }],
      receive: [{ kind: "skill", skillId: "escapeFeather" }],
    }),
  },
];

/** Filter templates by wallet eligibility, then weighted-pick one. */
export function pickTemplate(
  rng: SeededRng,
  eligible: (t: OfferTemplate) => boolean
): OfferTemplate | undefined {
  const pool = SHOP_OFFER_TEMPLATES.filter(eligible);
  if (pool.length === 0) return undefined;
  const totalWeight = pool.reduce((sum, t) => sum + t.weight, 0);
  let pickPoint = rng.next() * totalWeight;
  for (const t of pool) {
    pickPoint -= t.weight;
    if (pickPoint <= 0) return t;
  }
  return pool[pool.length - 1];
}
